import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Buildings, HouseLine, ShieldCheckered } from "@phosphor-icons/react";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState(new Set());
  const [isReviewer, setIsReviewer] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [license, setLicense] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [licensePath, setLicensePath] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleRole = (role) => {
    const next = new Set(roles);
    if (next.has(role)) next.delete(role); else next.add(role);
    setRoles(next);
  };

  const uploadLicense = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLicensePath(data.path);
      toast.success("License uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const submit = async () => {
    if (roles.size === 0) {
      toast.error("Pick at least one role");
      return;
    }
    if (roles.has("appraiser") && !paypalEmail) {
      toast.error("PayPal email required for appraisers");
      return;
    }
    setSaving(true);
    try {
      await api.post("/onboarding", {
        roles: Array.from(roles),
        is_reviewer: isReviewer,
        paypal_email: paypalEmail || null,
        license_number: license || null,
        license_state: licenseState || null,
        license_doc_path: licensePath,
      });
      await refresh();
      toast.success("Welcome aboard");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout hideNav>
      <div className="max-w-2xl mx-auto py-8 lg:py-16">
        <div className="kicker">Welcome, {user?.name?.split(" ")[0]}</div>
        <h1 className="display-serif text-4xl sm:text-5xl mt-3">Pick how you&apos;ll use the marketplace.</h1>
        <p className="mt-3 text-zinc-600">You can hold both roles — bid on jobs and also order appraisals.</p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <RoleCard
            testid="role-client"
            active={roles.has("client")}
            onClick={() => toggleRole("client")}
            Icon={HouseLine}
            title="Client"
            body="I'm a lender, attorney or homeowner ordering appraisals."
          />
          <RoleCard
            testid="role-appraiser"
            active={roles.has("appraiser")}
            onClick={() => toggleRole("appraiser")}
            Icon={Buildings}
            title="Appraiser"
            body="I'm licensed and want to bid on residential jobs."
          />
        </div>

        {roles.has("appraiser") && (
          <div className="mt-10 surface-card p-6 lg:p-8 space-y-5" data-testid="appraiser-details">
            <div className="flex items-center gap-2">
              <ShieldCheckered size={20} className="text-[#115E59]" weight="duotone" />
              <h3 className="display-serif text-2xl">Appraiser details</h3>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="paypal">PayPal email (for payouts) *</Label>
                <Input id="paypal" data-testid="onb-paypal-input" value={paypalEmail} onChange={e => setPaypalEmail(e.target.value)} placeholder="you@paypal.com" />
              </div>
              <div>
                <Label htmlFor="license">License number</Label>
                <Input id="license" data-testid="onb-license-input" value={license} onChange={e => setLicense(e.target.value)} placeholder="e.g., CR12345" />
              </div>
              <div>
                <Label htmlFor="licstate">License state</Label>
                <select
                  id="licstate"
                  data-testid="onb-license-state-select"
                  value={licenseState}
                  onChange={e => setLicenseState(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm"
                >
                  <option value="">Select…</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="lic-doc">License document (PDF or image)</Label>
                <input id="lic-doc" data-testid="onb-license-doc-input" type="file" accept="application/pdf,image/*" onChange={uploadLicense} className="mt-1 w-full text-sm" />
                {licensePath && <div className="text-xs text-emerald-700 mt-1">Uploaded ✓</div>}
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="onb-reviewer-toggle">
              <Checkbox checked={isReviewer} onCheckedChange={setIsReviewer} className="mt-1" />
              <span>
                <span className="font-medium">Opt into the reviewer pool</span>
                <span className="block text-sm text-zinc-600">Earn a fee reviewing other appraisers&apos; completed work.</span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-10 flex gap-3">
          <button onClick={submit} disabled={saving} className="btn-ink" data-testid="onb-continue-btn">
            {saving ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
    </PageLayout>
  );
}

function RoleCard({ testid, active, onClick, Icon, title, body }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-2xl p-6 border transition-all duration-200 ${
        active
          ? "border-[#115E59] bg-[#115E59] text-white shadow-[0_10px_30px_rgba(17,94,89,0.25)]"
          : "border-zinc-200 bg-white hover:-translate-y-0.5 hover:shadow-md"
      }`}
    >
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${active ? "bg-white/15 text-white" : "bg-[#CCFBF1] text-[#115E59]"}`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="display-serif text-2xl mt-3">{title}</div>
      <div className={`text-sm mt-1 ${active ? "text-white/85" : "text-zinc-600"}`}>{body}</div>
    </button>
  );
}
